import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import Modal, { ModalBody, ModalFooter } from 'Components/Modal';
import { Input, Form, Select, Button, Checkbox } from 'Components';
//Map
import Map from 'Components/Map'
import MapObject from 'Components/Map/providers/mapObject'
import InputSearchMap from 'Components/Map/components/InputSearchMap';
// Utils
import { TypeObjectMap } from 'Components/Map/providers/enums'
import './index.scss';
import { NotificationManager } from 'Components/Notifications';

import { createSelector } from 'reselect'
import { useDataGridThrottler } from 'Core/hooks';
import { getUnitsNaturalOrder } from 'Core/data/Helpers'
import { unitSelectors } from 'Core/realtime/redux/unitSlice';
import { lastReadingSelectors } from 'Core/realtime/redux/lastReadingSlice';


// Actions
import {
    catalogLoad,
    createStop,
    clearRedux,
    openStopCreateEdit,
    getLandmarksAndGeofences as getKmls
} from 'Redux/actions';

const getUnitsCatalog = createSelector([state => unitSelectors.selectAll(state)], getUnitsNaturalOrder);
const getReadingsCatalog = (state) => lastReadingSelectors.selectEntities(state);
const getVehicleTags = (state) => state.realtimeMapUtilitiesVehiclesListRedux.tagsVehicleList;

/*********************************************************
 * Get information from pagination elements
*********************************************************/
const getData = createSelector(
    [getUnitsCatalog, getReadingsCatalog, getVehicleTags],
    (unitsList, readings, vehicleTags) => {
        let result = []
        unitsList?.forEach((unit) => {
            try {
                if (unit.id == "-1" || (!unit.id && !unit.CUnits_id)) return
                const idUnit = unit.id

                let lastReading = readings?.[idUnit]
                const lat = lastReading?.latitude || unit?.latitude || unit?.lat
                const lng = lastReading?.longitude || unit?.longitude || unit?.lng
                const label = unit?.label || lastReading?.deviceLabel;
                if (!Array.isArray(unit?.tags) || unit.tags.length === 0) {
                    unit = { ...unit, tags: [{ color: "000000", hasChildren: false, id: -1, label: "Untagged", parentId: null, path: "" }] }
                }

                //filter unit for existing tag
                unit = {
                    ...unit,
                    tags: !vehicleTags?.length ? unit.tags : unit.tags?.filter(tag => vehicleTags?.some(vehicleTag => tag?.id == -1 || vehicleTag?.id === tag?.id))
                }

                // if the unit no have tags or none is the group "Untagged", it is omitted
                if (!unit?.tags?.length && !unit?.tags?.some(tag => tag.id === -1)) return; // No is entered on the list of vehicle because no have permisions

                if (!lastReading?.haveLatLng) return; // No is entered on the list of vehicle because no have lat and lng

                if (!lastReading?.address) return; // No is entered on the list of vehicle because no have address

                const item = {
                    id: unit.id || lastReading?.deviceId,
                    label: label,
                    lat: lat ? parseFloat(lat) : 0,
                    lng: lng ? parseFloat(lng) : 0,
                    latitude: lat ? parseFloat(lat) : 0,
                    longitude: lng ? parseFloat(lng) : 0,
                    haveLatLng: lastReading?.haveLatLng, // this vehicle no have lat and lng, cant show on map
                    address: lastReading?.address,
                    fulladdress: lastReading?.address?.formatted || '',
                    changeLabel: (unit?.label != label),
                    changeLatLng: lastReading?.changeLatLng,
                    lastReading: lastReading,
                    tags: unit.tags || []
                }
                result.push(item);
            } catch (e) {
                console.log('e', e)
                NotificationManager.error('Error', `Error src/components/Map/components/FromToMap/index.js to create show list of vehicles - ${e}`, 3000, null, null, 'error');
            }
        });
        return result
    }
)

const RouteCreateEdit = (props) => {
    const dispatch = useDispatch();
    const { callBack, driver } = props;
    const { messages } = useIntl();
    const [error, setError] = useState('');
    const [formData, setFormData] = useState({});
    const [position, setPosition] = useState({});
    const formAddStop = useRef();
    const loadingUnits = useSelector(state => state.catalogsRedux.loadingUnits);

    const landmarks = useSelector((state) => state.catalogsRealtimeRedux.landmarks);
    const loadingLandmarks = useSelector((state) => state.catalogsRealtimeRedux.loadingLandmarks);

    const addressTypes = [
        { label: messages['routes_Address'], value: messages['routes_Address'] },
        { label: messages['routes_Unit'], value: messages['routes_Unit'] },
        { label: messages['routes_Landmark'], value: messages['routes_Landmark'] },
        { label: messages['routes_mapClick'], value: messages['routes_mapClick'] }
    ];

    const mapRef = useRef()
    const {
        openCreateEdit,
        response,
        loadingUpdate
    } = useSelector((state) => state.RoutesRedux);

    /*********************************************************
     * use compute data to sync values showing in table
    *********************************************************/
    const computedData = useSelector(state => getData(state) || []);
    const propertiesToValidate = useMemo(() => ["label", "lat", "lng", "changeLatLng", "haveLatLng", "tags"], []);

    // Hook handles both Throttled Arrays for `<Map/>` and imperative delta diffing for markers via `handleMapUpdates`
    const units = useDataGridThrottler(
        computedData,
        1000,
        () => { },
        "id",
        propertiesToValidate
    );

    useEffect(() => {
        dispatch(getKmls());
    }, []);

    useEffect(() => {
        if (!response.error && response?.stop?.id) {
            NotificationManager.success(
                messages['FSM_SETTINGS_ROUTE_CREATE_DONE'],
                messages['success'],
                3000,
                null,
                null,
                'Success',
            );

            if (!formData?.addMoreStops) {
                handleClose();
            } else {
                setFormData({ addMoreStops: true })
            }
        }

    }, [response]);

    useEffect(() => {
        if (units.length > 0)
            setFormData({ type: messages['routes_Unit'] });

    }, [units]);

    useEffect(() => {
        if (error && error.length) {
            NotificationManager.warning(
                error,
                messages['error'],
                3000,
                null,
                null,
                'warning'
            );
            setError('')
        }
    }, [error])

    const handleClose = useCallback(() => {
        dispatch(clearRedux("FSM_ROUTES_STOP", {
            response: {}
        }));

        setFormData({})
        setPosition({})
        setError('')
        callBack();
    });

    const onMapClick = (latLng) => {
        setPosition(latLng);
        mapRef?.current?.removeMapObjects(TypeObjectMap.Marker)
        const mapObject = new MapObject().buildMarker({
            id: 'fsmRouteLocation',
            position: { lat: latLng.lat, lng: latLng.lng },
            label: '',
            heading: 0,
            color: '000',
            eventName: 'no event',
            draggable: false
        });

        mapRef?.current?.addMapObject(mapObject);
        mapRef?.current?.centerPointOnMap(latLng?.lat - 0.1500, latLng?.lng + 0.4000, 11);
    }

    const handleSubmit = useCallback(() => {
        if (!position?.lat)
            return setError(messages['FSM_SETTINGS_ROUTE_ERROR_CREATE']);

        dispatch(createStop({
            "statusId": 1,
            "name": formData.name,
            "latitude": position?.lat,
            "longitude": position?.lng,
            "description": formData.note,
            "sequence": 0,
            "address": (!formData?.address) ? formData.name : formData?.address,
            "driverId": driver?.driverId
        }));

    });

    const initMap = () => {
        mapRef?.current?.initMapClick(onMapClick);
    }


    return (
        <Modal
            title={messages["FSM_SETTINGS_ROUTE_NEW"]}
            open={openCreateEdit}
            position="right"
            loading={loadingUpdate || loadingLandmarks}
            size="medium"
            handleClose={handleClose}
        >
            <ModalBody>
                <div style={{ padding: '14px 24px 2px 24px' }} className='createFsmStop' >
                    {/* {
                        error &&
                        <Alert severity="error" onClose={() => setError('')}>{error}</Alert>
                    } */}

                    <div style={{ height: "calc(100vh - 200px)", overflow: "hidden auto" }}>
                        <div style={{ height: 350, marginBottom: 12, overflow: 'hidden', position: 'relative' }}>
                            <label className="input-normal-label" >
                                {messages['FSM_SETTINGS_ROUTE_PREVIEW']}
                            </label>
                            <Map ref={mapRef} onMapClick={onMapClick} style={{ marginTop: 10 }}>
                            </Map>
                        </div>

                        <Form ref={formAddStop} onValidSubmit={handleSubmit}>

                            <div className="form-group" >
                                <div style={{ marginBottom: 12 }}>
                                    <Select
                                        id="type"
                                        name="type"
                                        value={formData.type || null}
                                        label={messages['routes_selectAddressType']}
                                        placeholder={messages['routes_selectAddressType']}
                                        onChange={(value) => {
                                            setFormData({
                                                ...formData,
                                                type: value,
                                                unit: null,
                                                name: null,
                                                address: null,
                                                landmark: null
                                            });
                                            if (value && value?.value === messages['routes_mapClick']) {
                                                initMap()
                                            } else {
                                                mapRef?.current?.removeMapClick()
                                            }

                                        }}
                                        isSearchable
                                        required={true}
                                        requiredMessage={messages['required_field']}
                                        options={addressTypes}
                                        textField="label"
                                        valueField="value"
                                    />
                                </div>

                                {
                                    (formData?.type && formData?.type?.value === messages['routes_Unit'])
                                    &&
                                    <div style={{ marginBottom: 12 }}>
                                        <Select
                                            id="unit"
                                            name="unit"
                                            label={messages['routes_selectUnit']}
                                            placeholder={messages['routes_selectUnit']}
                                            value={formData.unit || null}
                                            onChange={(value) => {
                                                const { lastReading } = value;
                                                const { latitude, longitude, address } = lastReading;
                                                setFormData({ ...formData, unit: value, name: `${address?.address}, ${address?.city}, ${address?.state}`, address: `${address?.address}, ${address?.city}, ${address?.state}` });
                                                onMapClick({ lat: latitude, lng: longitude });
                                            }}
                                            loading={loadingUnits}
                                            options={units}
                                            textField={'label'}
                                            valueField={'id'}
                                            isSearchable
                                            required
                                            requiredMessage={messages['required_field']}
                                        />
                                    </div>
                                }


                                {
                                    (formData?.type && formData?.type?.value === messages['routes_Address'])
                                    &&
                                    <div style={{ marginBottom: 12 }}>
                                        <label className="input-normal-label">
                                            {messages['routes_selectAddress']} <span className="text-danger"> *</span>
                                        </label>

                                        <div >
                                            <InputSearchMap
                                                normalView
                                                noMap
                                                onGetAddress={(value) => {
                                                    if (value && value?.length) {
                                                        const address = value[0];
                                                        setFormData({ ...formData, name: address?.fulladdress, address: address?.fulladdress });

                                                        onMapClick({ lat: address.lat, lng: address.lng });
                                                    }
                                                }}
                                                onClear={() => {
                                                }}
                                                draggable={false}
                                                noOptionsText={messages['noOptions']}
                                                placeholder={messages['search']}
                                            />
                                        </div>
                                    </div>
                                }

                                {
                                    (formData?.type && formData?.type?.value === messages['routes_Landmark'])
                                    &&
                                    <div style={{ marginBottom: 12 }}>
                                        <Select
                                            id="landmarks_routes"
                                            name="landmarks_routes"
                                            label={messages['FSM_SETTINGS_ROUTE_LANDMARK']}
                                            placeholder={messages['FSM_SETTINGS_ROUTE_LANDMARK']}
                                            value={formData?.landmark || null}
                                            onChange={(value) => {
                                                setFormData({
                                                    ...formData,
                                                    landmark: value,
                                                    name: `${value?.address}`,
                                                    address: `${value?.address}`
                                                });

                                                onMapClick({ lat: value.latitude, lng: value.longitude });
                                            }}
                                            loading={loadingLandmarks}
                                            options={landmarks || []}
                                            textField={'name'}
                                            valueField={'id'}
                                            isSearchable
                                            required
                                            requiredMessage={messages['required_field']}
                                        />
                                    </div>
                                }

                                <div style={{ marginBottom: 10 }}>
                                    <Input
                                        id="name"
                                        type="text"
                                        label={messages['FSM_TITLE_STOP']}
                                        placeholder={messages['FSM_TITLE_STOP']}
                                        value={formData?.name || ""}
                                        onChange={(value) =>
                                            setFormData({ ...formData, name: value })
                                        }
                                        required
                                        requiredMessage={messages['required_field']}

                                    />
                                </div>
                                <div style={{ marginBottom: 10 }}>
                                    <Input
                                        id="note"
                                        name="note"
                                        type="textarea"
                                        label={messages['routes_notes']}
                                        placeholder={messages['routes_notes']}
                                        value={formData.note || ""}
                                        onChange={(value) => {
                                            setFormData({ ...formData, note: value });
                                        }}
                                        rows="3"
                                        maxLength={200}
                                    />
                                </div>

                                <div style={{ marginBottom: 10 }}>
                                    <Checkbox
                                        id={`fsm-add-route`}
                                        label={messages['FSM_SETTINGS_ROUTE_MORE_STOPS']}
                                        value={formData?.addMoreStops}
                                        onChange={(checked) => {
                                            setFormData({ ...formData, addMoreStops: checked });
                                        }}
                                    />
                                </div>
                            </div>
                        </Form>
                    </div>


                </div>
            </ModalBody>

            <ModalFooter style={{ boxShadow: "2px 2px 2px 1px rgb(2 0 0 / 94%)" }} >
                <Button
                    disabled={loadingUpdate}
                    onClick={() => {
                        setFormData({})
                        setPosition({})
                        setError('');
                        dispatch(openStopCreateEdit(false))
                        handleClose()
                    }}
                    className="btn-cancel"
                >
                    {messages['cancel']}
                </Button>

                <Button
                    disabled={loadingUpdate}
                    onClick={(e) => {
                        if (e) e.preventDefault();
                        formAddStop.current.submit();
                    }}
                    className="btn-blue"
                >
                    {messages['save']}
                </Button>
            </ModalFooter>
        </Modal>
    )
}

export default RouteCreateEdit;